import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./MagmaSelect.css";

function asString(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function getNextEnabledIndex(options, currentIndex, step) {
  if (!options.length) return -1;
  let index = currentIndex;
  for (let i = 0; i < options.length; i += 1) {
    index = (index + step + options.length) % options.length;
    if (!options[index].disabled) {
      return index;
    }
  }
  return currentIndex;
}

export default function MagmaSelect({
  id,
  name,
  className = "",
  value = "",
  onChange,
  children,
  disabled = false,
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const normalizedValue = asString(value);

  const options = useMemo(() => {
    return React.Children.toArray(children)
      .filter(node => React.isValidElement(node) && node.type === "option")
      .map((node, index) => ({
        key: node.key ?? `${index}-${asString(node.props.value)}`,
        value: asString(node.props.value),
        label: node.props.children,
        disabled: Boolean(node.props.disabled),
      }));
  }, [children]);

  const selectedIndex = useMemo(
    () => options.findIndex(item => item.value === normalizedValue),
    [options, normalizedValue]
  );

  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  const updateMenuPosition = () => {
    if (!triggerRef.current || typeof window === "undefined") {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = event => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const handleEscape = event => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    updateMenuPosition();
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const emitChange = nextValue => {
    if (typeof onChange !== "function") return;
    onChange({
      target: { value: nextValue, id, name },
      currentTarget: { value: nextValue, id, name },
    });
  };

  const handleOptionSelect = option => {
    if (disabled || option.disabled) return;
    emitChange(option.value);
    setOpen(false);
  };

  const handleTriggerKeyDown = event => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(prev => !prev);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = getNextEnabledIndex(options, selectedIndex >= 0 ? selectedIndex : -1, step);
      if (nextIndex >= 0 && nextIndex !== selectedIndex) {
        emitChange(options[nextIndex].value);
      }
      if (!open) {
        setOpen(true);
      }
    }
  };

  const triggerClassName = `magma-select-trigger${className ? ` ${className}` : ""}`;
  const menuNode = open ? (
    <ul
      ref={menuRef}
      className="magma-select-menu magma-select-menu-portal"
      role="listbox"
      aria-labelledby={id}
      style={menuStyle}
    >
      {options.map(option => (
        <li key={option.key} role="option" aria-selected={option.value === normalizedValue}>
          <button
            type="button"
            className={`magma-select-option${option.value === normalizedValue ? " is-selected" : ""}${option.disabled ? " is-disabled" : ""}`}
            disabled={disabled || option.disabled}
            onClick={() => handleOptionSelect(option)}
          >
            {option.label}
          </button>
        </li>
      ))}
    </ul>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`magma-select${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}`}
    >
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={`magma-select-label${selectedOption ? "" : " is-placeholder"}`}>
          {selectedOption ? selectedOption.label : "Selecione"}
        </span>
        <span className="magma-select-caret" aria-hidden="true" />
      </button>

      {name ? <input type="hidden" name={name} value={normalizedValue} /> : null}

      {menuNode && typeof document !== "undefined" ? createPortal(menuNode, document.body) : menuNode}
    </div>
  );
}
